// components/ArticleList.tsx
import React, { useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Card } from '@mui/material';
import { azul, black, lightGray, primary, rojoerror, rojoOscuro, white, whiteSmoke } from '@/constants/Colors';
import { imagenDefault, obtenerImagenes, obtenerURLDeArticulo, obtenerURLLibroByCategoriaIdArticle } from '@/util/Articulos';
import { useServices } from '@/context/ServicesProvider';
import { DIAS_VENTA_CADUCADA, EstadoBeseifCompra, EstadosBeseifFinalizacion, EstadosCompraBeseif, GetArticuloCompraBeseif } from '@/objects/Beseif';
import ImageRound from '@/components/elements/ImageRound';
import Texto from '@/components/elements/Texto';
import { dateToTextTForm, estaCaducada, obtenerHoraTForm } from '@/util/MagicDate';
import { useTranslation } from 'react-i18next';
import { textosMini } from '@/constants/Dimens';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNotificationContext } from '@/context/NotificationContext';
import EstadoCompra from '../EstadoCompra';
import BadgeCustom from '@/components/elements/Badge';
import CardUser from '@/components/widgets/libro/CardUser';
import { filtarMoneda } from '@/util/Monedas';
import Boton from '@/components/elements/Boton';

interface Props {
  getArticuloCompraBeseif: GetArticuloCompraBeseif;
}

const RowDatosBeseif: React.FC<Props> = ({ getArticuloCompraBeseif }) => {

  const { t } = useTranslation();

  const { beseifQueries } = useServices();
  const { userData } = useNotificationContext();

  //Estados de seguimiento
  const [estadoSeguimiento, setEstadoSeguimiento] = React.useState<EstadoBeseifCompra[]>();
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const [imagenArticulo, setImagenArticulo] = React.useState<string>(obtenerImagenes(getArticuloCompraBeseif.imagen!, "").image_preview);
  const [disabled, setDisabled] = React.useState<boolean>(false);

  useEffect(() => {
    //Comprueba si esta caducado
    if (estaCaducada(getArticuloCompraBeseif.fecha_compra, DIAS_VENTA_CADUCADA)) {
      setDisabled(true);
    }
    if (EstadosBeseifFinalizacion.includes(getArticuloCompraBeseif.accion as EstadosCompraBeseif)) {
      setDisabled(true);
    }
  }, [getArticuloCompraBeseif.accion, getArticuloCompraBeseif.fecha_compra]);

  const obtenerSeguimiento = () => {

    const id_usuario_comprador = getArticuloCompraBeseif.comprador.id_user || 1;
    const id_usuario_vendedor = getArticuloCompraBeseif.vendedor.id_user || 1;

    beseifQueries?.obtener_estados_compra_beseif_usuario_comprador(id_usuario_vendedor, id_usuario_comprador, getArticuloCompraBeseif.id_articulo, getArticuloCompraBeseif.categoria).then((response) => {
      if (response.status == 200) {
        setEstadoSeguimiento(response.data);
      }
    }).catch((error) => {

    });
  };

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);

    if (isExpanded && estadoSeguimiento === undefined) {
      obtenerSeguimiento();
    }
  };

  const esFinalizado = (): boolean => {
    return EstadosBeseifFinalizacion.includes(getArticuloCompraBeseif.accion as EstadosCompraBeseif);
  }

  const abrirLibro = () => {
    //Redirige a la pagina del libro comprado
    window.open(obtenerURLLibroByCategoriaIdArticle(getArticuloCompraBeseif.categoria, getArticuloCompraBeseif.id_articulo), '_blank');
  };

  const idUsuarioSeguimiento = getArticuloCompraBeseif.comprado
    ? getArticuloCompraBeseif.comprador.id_user
    : getArticuloCompraBeseif.vendedor.id_user;


  return (
    <Card
      style={{
        cursor: "pointer", display: 'flex', flexDirection: 'row',
        alignItems: 'center', justifyContent: 'space-between',
        width: window.innerWidth < 600 ? "100%" : "90%",
        margin: '0 auto',
        backgroundColor: disabled ? whiteSmoke : white,
        color: disabled ? lightGray : black,
        padding: "20px",
        boxSizing: 'border-box',
      }}>
      <article style={{ display: 'flex', flexDirection: 'column', width: "100%", gap: "12px" }}>
        {!getArticuloCompraBeseif.comprado && (
          <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Texto color={disabled ? lightGray : black} fontSize={textosMini}>{t("menu:secciones.mis_compras.seguimiento.puedes_cancelar_la_venta")}</Texto>
          </section>
        )}
        {getArticuloCompraBeseif.fecha_recogida &&
          <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Texto color={disabled ? lightGray : black} fontSize={textosMini}>{t("menu:secciones.mis_compras.seguimiento.fecha_maxima_envio", { fecha: getArticuloCompraBeseif.fecha_recogida })}</Texto>
          </section>
        }
        <section style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', width: "100%", gap: "10px" }}>
          <section style={{ position: 'relative', flex: '0 0 auto' }}>
            <ImageRound
              src={imagenArticulo}
              alt={t("menu:secciones.mensajes.imagen_articulo" + getArticuloCompraBeseif.titulo)}
              width={70}
              height={70}
              style={disabled ? { filter: 'grayscale(100%)' } : {}}
              onError={() => setImagenArticulo(imagenDefault())}
            />
          </section>
          <section style={{ display: 'flex', flexDirection: 'column', flex: '1 1 240px', minWidth: 0 }}>
            <Texto color={disabled ? lightGray : black}>{getArticuloCompraBeseif.titulo}</Texto>
            <Texto color={disabled ? lightGray : black} fontSize={textosMini}>{t("menu:secciones.mis_compras.seguimiento.fecha_de_compra") + ": " + dateToTextTForm(getArticuloCompraBeseif.fecha_compra, t) + " " + obtenerHoraTForm(getArticuloCompraBeseif.fecha_compra)}</Texto>
          </section>
          <section style={{ display: 'flex', flexDirection: 'column', flex: '1 1 220px', minWidth: 0 }}>
            <Texto color={disabled ? lightGray : black} fontSize={textosMini}>{t("menu:secciones.mis_compras.seguimiento.puedes_contactar_con_el_vendedor")}</Texto>
            <Boton height={"30px"} fullWidth={true} texto={t("menu:secciones.mis_compras.seguimiento.ver_libro")} onClick={abrirLibro} />
          </section>
        </section>
        {idUsuarioSeguimiento !== undefined && (
          <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: "10px" }}>
            <CardUser id_usuario={idUsuarioSeguimiento} modo_seguimiento={true} />
          </section>
        )}
        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          disableGutters
          elevation={0}
          square
          sx={{
            width: '100%',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '12px',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              minHeight: 44,
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
                justifyContent: 'flex-end',
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                margin: '8px 0',
              },
            }}
          >
            <Texto style={{ width: "100%" }}>{t("menu:secciones.mis_compras.seguimiento.titulo")}</Texto>
            <BadgeCustom
              texto={
                esFinalizado() ? t("menu:secciones.mis_compras.seguimiento.estados." + getArticuloCompraBeseif.accion)
                  :
                  estaCaducada(getArticuloCompraBeseif.fecha_compra, DIAS_VENTA_CADUCADA)
                    ? t("menu:secciones.mis_compras.seguimiento.estados.caducada")
                    : t("menu:secciones.mis_compras.seguimiento.estados." + getArticuloCompraBeseif.accion)
              }
              backgroundColor={esFinalizado() ? primary :
                estaCaducada(getArticuloCompraBeseif.fecha_compra, DIAS_VENTA_CADUCADA) ? rojoerror
                  : azul}
              color={white}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ paddingTop: 0 }}>
            {estadoSeguimiento ? <EstadoCompra estadoBeseifCompra={estadoSeguimiento} /> : <Texto>{t("menu:secciones.mis_compras.seguimiento.no_info")}</Texto>}
          </AccordionDetails>
        </Accordion>
      </article>
    </Card>
  );
}

export default RowDatosBeseif;

